import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import "./AuthPage.css";

const REMEMBERED_EMAIL_KEY = "anina.auth.rememberedEmail";

function recoveryTokensFromUrl() {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  if (hash.get("type") !== "recovery") return null;
  const accessToken = hash.get("access_token");
  const refreshToken = hash.get("refresh_token");
  return accessToken && refreshToken ? { accessToken, refreshToken } : null;
}

function initialMode() {
  const params = new URLSearchParams(window.location.search);
  const authMode = params.get("auth");

  if (authMode === "reset-password") return "reset";
  if (authMode === "create-account") return "register";

  return "login";
}

function readRememberedEmail() {
  try {
    return window.localStorage.getItem(REMEMBERED_EMAIL_KEY) || "";
  } catch {
    return "";
  }
}

function saveRememberedEmail(email) {
  try {
    window.localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim().toLowerCase());
  } catch {
    // Browser storage may be unavailable; sign-in should still continue safely.
  }
}

function clearRememberedEmail() {
  try {
    window.localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  } catch {
    // Browser storage may be unavailable; sign-in should still continue safely.
  }
}

function PasswordVisibilityIcon({ visible }) {
  return visible ? (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 3l18 18" />
      <path d="M10.6 10.6a2 2 0 002.8 2.8" />
      <path d="M9.9 4.2A10.7 10.7 0 0112 4c5.5 0 9 5 9 5a17.6 17.6 0 01-3.1 3.5" />
      <path d="M6.6 6.6C4.4 8 3 10 3 10s3.5 5 9 5c1 0 2-.2 2.8-.5" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 12s3.5-5 9-5 9 5 9 5-3.5 5-9 5-9-5-9-5z" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}

function AuthPage() {
  const auth = useAuth();
  const recoveryHandledRef = useRef(false);
  const rememberedEmailRef = useRef(readRememberedEmail());
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState(rememberedEmailRef.current);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [showConfirmPasswordText, setShowConfirmPasswordText] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(Boolean(rememberedEmailRef.current));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(auth.message || "");
  const [error, setError] = useState("");

  useEffect(() => {
    if (recoveryHandledRef.current) return;
    const recovery = recoveryTokensFromUrl();
    if (!recovery) return;
    recoveryHandledRef.current = true;

    setBusy(true);
    auth.establishRecoverySession(recovery.accessToken, recovery.refreshToken)
      .then(() => {
        setMode("reset");
        setNotice("Recovery verified. Choose a new password.");
        window.history.replaceState({}, document.title, `${window.location.pathname}?auth=reset-password`);
        window.dispatchEvent(new Event("anina:auth-route"));
      })
      .catch((recoveryError) => setError(recoveryError.message))
      .finally(() => setBusy(false));
  }, [auth]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");

    try {
      if (mode === "login") {
        await auth.login(email, password);
        if (rememberEmail) {
          saveRememberedEmail(email);
        } else {
          clearRememberedEmail();
        }
      } else if (mode === "register") {
        if (password !== confirmPassword) throw new Error("Passwords do not match.");
        const result = await auth.register(email, password);
        if (result.confirmation_required) {
          setNotice(result.message);
          setMode("login");
          setPassword("");
          setConfirmPassword("");
          setShowPasswordText(false);
          setShowConfirmPasswordText(false);
        }
      } else if (mode === "forgot") {
        const result = await auth.requestPasswordReset(email);
        setNotice(result.message);
      } else if (mode === "reset") {
        if (password !== confirmPassword) throw new Error("Passwords do not match.");
        await auth.updatePassword(password);
        setNotice("Password updated. Your secure session is active.");
        setPassword("");
        setConfirmPassword("");
        setShowPasswordText(false);
        setShowConfirmPasswordText(false);
        window.history.replaceState({}, document.title, window.location.pathname);
        window.dispatchEvent(new Event("anina:auth-route"));
      }
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setBusy(false);
    }
  };

const changeModeSafely = (nextMode) => {
  setPassword("");
  setConfirmPassword("");
  setShowPasswordText(false);
  setShowConfirmPasswordText(false);
  setError("");
  setNotice("");
  setMode(nextMode);
};


  const handleRememberEmailChange = (event) => {
    const shouldRemember = event.target.checked;
    setRememberEmail(shouldRemember);
    if (!shouldRemember) {
      clearRememberedEmail();
    }
  };

  const title = mode === "register" ? "Create your AninaTrade account" : mode === "forgot" ? "Reset your password" : mode === "reset" ? "Choose a new password" : "Welcome back";
  const showEmail = mode !== "reset";
  const showPassword = mode === "login" || mode === "register" || mode === "reset";

  return (
    <main className="authPage">
      <section className="authBrandPanel">
        <div className="authBrandMark">A</div>
        <span className="authEyebrow">ANINATRADE SECURE ACCESS</span>
        <h1>Trade intelligence with an identity boundary built for real accounts.</h1>
        <p>NINA personalization, portfolio continuity, Academy progress, and future broker permissions stay attached to the authenticated user—not the browser.</p>
        <div className="authTrustGrid">
          <span>✓ Server-verified sessions</span>
          <span>✓ Role-based owner access</span>
          <span>✓ Cross-device identity</span>
          <span>✓ Password recovery</span>
        </div>
      </section>

      <section className="authFormPanel" aria-labelledby="auth-title">
        <div className="authFormCard">
          <span className="authFormEyebrow">AI TRADING OPERATING SYSTEM</span>
          <h2 id="auth-title">{title}</h2>
          <p>{mode === "register" ? "Create one secure identity for your AninaTrade workspace." : mode === "forgot" ? "We’ll send a recovery link if an account exists." : mode === "reset" ? "Use at least 12 characters for your new password." : "Sign in to continue to your protected workspace."}</p>

          <form onSubmit={submit}>
            {showEmail && (
              <label>
                Email
                <input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
              </label>
            )}
            {showPassword && (
              <label>
                Password
                <div className="authPasswordField">
                  <input
                    type={showPasswordText ? "text" : "password"}
                    minLength={12}
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <button
                    className="authPasswordToggle"
                    type="button"
                    aria-label={showPasswordText ? "Hide password" : "Show password"}
                    aria-pressed={showPasswordText}
                    title={showPasswordText ? "Hide password" : "Show password"}
                    onClick={() => setShowPasswordText((visible) => !visible)}
                  >
                    <PasswordVisibilityIcon visible={showPasswordText} />
                  </button>
                </div>
              </label>
            )}
            {(mode === "register" || mode === "reset") && (
              <label>
                Confirm password
                <div className="authPasswordField">
                  <input
                    type={showConfirmPasswordText ? "text" : "password"}
                    minLength={12}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    required
                  />
                  <button
                    className="authPasswordToggle"
                    type="button"
                    aria-label={showConfirmPasswordText ? "Hide confirm password" : "Show confirm password"}
                    aria-pressed={showConfirmPasswordText}
                    title={showConfirmPasswordText ? "Hide confirm password" : "Show confirm password"}
                    onClick={() => setShowConfirmPasswordText((visible) => !visible)}
                  >
                    <PasswordVisibilityIcon visible={showConfirmPasswordText} />
                  </button>
                </div>
              </label>
            )}

            {mode === "login" && (
              <label className="authRememberRow">
                <input
                  type="checkbox"
                  checked={rememberEmail}
                  onChange={handleRememberEmailChange}
                />
                <span>
                  <strong>Remember my email</strong>
                  <small>Only your email address is saved on this device. Your password is never saved.</small>
                </span>
              </label>
            )}

            {error && <div className="authAlert authAlertError" role="alert">{error}</div>}
            {notice && <div className="authAlert authAlertSuccess" role="status">{notice}</div>}

            <button className="authPrimaryButton" type="submit" disabled={busy}>{busy ? "Working…" : mode === "register" ? "Create account" : mode === "forgot" ? "Send reset link" : mode === "reset" ? "Update password" : "Sign in"}</button>
          </form>

          <div className="authSecondaryActions">
  {mode === "login" && (
    <>
      <button type="button" onClick={() => changeModeSafely("forgot")}>
        Forgot password?
      </button>
      <button type="button" onClick={() => changeModeSafely("register")}>
        Create account
      </button>
    </>
  )}
  {mode !== "login" && mode !== "reset" && (
    <button type="button" onClick={() => changeModeSafely("login")}>
      Back to sign in
    </button>
  )}
</div>

          <button
            type="button"
            className="authBackHomeButton"
            onClick={() => window.location.assign("/")}
          >
            Back to Home
          </button>

          <small className="authSecurityNote">AninaTrade does not store your password in React or browser local storage.</small>
        </div>
      </section>
    </main>
  );
}

export default AuthPage;
